import cuid2 from "@paralleldrive/cuid2"
import { createFileRoute, Navigate, NavigateOptions } from "@tanstack/react-router"
import { useMemo } from "react"

import { Spinner } from "@/components/Spinner"
import { db } from "@/database/database"
import { useOrderedSteps, usePhotosWithDetailsByType } from "@/database/hooks"
import { logger } from "@/utils/logging"

export const Route = createFileRoute("/$sessionId/_session/_steps/wall/")({
  component: RouteComponent,
  // due to endless cycle in useSession hook after navigation from /address step
  loader: async ({ params }) => {
    const session = await db.session.get(params.sessionId)

    if (!session) {
      throw new Error("Session not found in local database")
    }

    return {
      session,
    }
  },
})

function RouteComponent() {
  const { sessionId } = Route.useParams()
  const {
    session: { numberOfWalls },
  } = Route.useLoaderData()

  const { data: orderedSteps, isLoading: isLoadingOrderedSteps } = useOrderedSteps(sessionId)
  const wallStep = useMemo(() => orderedSteps?.find((step) => step.name === "Wall"), [orderedSteps])

  const { data: wallPhotos, isLoading: isLoadingWallPhotos } = usePhotosWithDetailsByType({ sessionId, type: "Wall" })

  const wallNumbersArr = useMemo(() => Array.from({ length: numberOfWalls }, (_, i) => i + 1), [numberOfWalls])

  const hasEveryWallPhoto = useMemo(() => {
    if (!wallPhotos || isLoadingWallPhotos) return
    return wallNumbersArr.every((wallNum) => wallPhotos.some((photo) => photo.wallNumber === wallNum))
  }, [wallPhotos, isLoadingWallPhotos, wallNumbersArr])

  const firstIncompleteWallNumber = useMemo(() => {
    if (!wallPhotos || isLoadingWallPhotos) return
    return wallNumbersArr.find((wallNum) => !wallPhotos.some((photo) => photo.wallNumber === wallNum))
  }, [wallPhotos, isLoadingWallPhotos, wallNumbersArr])

  // Wall photo with the highest wallNumber — the "current" wall the user is working on.
  const lastWallPhoto = useMemo(() => {
    if (!wallPhotos || isLoadingWallPhotos) return

    const sortedByWallNumber = [...wallPhotos].sort((a, b) => (a.wallNumber ?? 0) - (b.wallNumber ?? 0))
    return sortedByWallNumber.findLast((photo) => photo.wallNumber !== undefined)
  }, [wallPhotos, isLoadingWallPhotos])

  // Whether the last wall still needs more details.
  // threshold: customer-configured `minDetails`, or `1` when unconfigured — user gets chance to add a detail on last wall.
  const lastWallNeedsDetails = useMemo(() => {
    if (!lastWallPhoto || lastWallPhoto.wallNumber === undefined) return false

    const threshold = Math.max(wallStep?.minDetails ?? 0, 1)
    return lastWallPhoto.details.length < threshold
  }, [lastWallPhoto, wallStep])

  const newWallId = useMemo(() => cuid2.createId(), [])

  /* Decides if user lands on wall detail guide or wall + details photo overview */
  const lastWallNavigation = useMemo<NavigateOptions | undefined>(() => {
    if (!lastWallPhoto || !lastWallPhoto.wallNumber) return

    //  wall has no details → show guide for wall detail
    const lastDetail = [...lastWallPhoto.details].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0]
    if (!lastDetail) {
      return {
        to: "/$sessionId/wall/$wallNumber/$wallId",
        params: { sessionId, wallNumber: lastWallPhoto.wallNumber.toString(), wallId: lastWallPhoto.id },
      }
    }

    // wall has details → show wall photo + details (current photo is the latest detail)
    return {
      to: "/$sessionId/wall/$wallNumber/$wallId/wallDetail/$wallDetailId",
      params: {
        sessionId,
        wallNumber: lastWallPhoto.wallNumber.toString(),
        wallId: lastWallPhoto.id,
        wallDetailId: lastDetail.id,
      },
      search: { currentPhotoId: lastDetail.id },
    }
  }, [lastWallPhoto, sessionId])

  /* Loading state */
  if (isLoadingWallPhotos || isLoadingOrderedSteps) {
    return <Spinner position="center" />
  }

  /* Last wall is missing details — go there BEFORE sending the user to
  capture the next wall. Earlier bare walls are intentionally ignored (we assume user skipped details on purpouse). */
  if (lastWallNeedsDetails) {
    logger.debug("[debug] Navigating to wall with missing details:", lastWallNavigation)
    return <Navigate {...lastWallNavigation} />
  }

  /* Navigate to the first incomplete wall photo */
  if (!hasEveryWallPhoto && firstIncompleteWallNumber) {
    logger.debug("[debug] Navigating to first incomplete wall photo:", { firstIncompleteWallNumber, newWallId })
    return (
      <Navigate
        to="/$sessionId/wall/$wallNumber/$wallId"
        params={{ sessionId, wallNumber: firstIncompleteWallNumber.toString(), wallId: newWallId }}
      />
    )
  }

  /* All main wall photos exist — land on the last wall so the user can choose to add more
  details or proceed to addNewWall themselves. */
  if (hasEveryWallPhoto) {
    logger.debug("[debug] Navigating to last wall / addNewWall:", lastWallNavigation)
    return <Navigate {...lastWallNavigation} />
  }

  return null
}
